import { useEffect, useLayoutEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { ensurePortalRoot } from '../shared/index.js';
import { createPortal } from 'react-dom';

export interface FloatingToolbarAnchor {
  /** getBoundingClientRect()-style rect in viewport coordinates. */
  getBoundingClientRect: () => {
    top: number;
    left: number;
    right: number;
    bottom: number;
    width: number;
    height: number;
  };
}

export interface FloatingToolbarProps {
  /** Anchor to position against — any object with a `getBoundingClientRect`
   *  method. Pass a `DOMRect`, an element, or a Selection's range. */
  anchor: FloatingToolbarAnchor | null;
  /** When false, the toolbar unmounts. */
  open: boolean;
  /** Preferred side relative to the anchor. Will flip if there isn't
   *  room on the preferred side. Default: `top`. */
  side?: 'top' | 'bottom';
  /** Alignment along the anchor's major axis. Default `center`. */
  align?: 'start' | 'center' | 'end';
  /** Pixel gap between anchor and toolbar. Default 8. */
  offset?: number;
  /** Render a tail/arrow pointing back at the anchor. The arrow follows
   *  the resolved side (top → tail points down, bottom → up) and tracks
   *  the anchor's horizontal centre, so the toolbar reads as one bubble
   *  rather than a free-floating chip. Default `false`. */
  arrow?: boolean;
  /** Children — typically a cluster of IconButtons. */
  children: ReactNode;
  /** Landmark label. */
  'aria-label': string;
  className?: string;
}

function getVisibleViewport(): { top: number; left: number; width: number; height: number } {
  if (typeof window === 'undefined') {
    return { top: 0, left: 0, width: 0, height: 0 };
  }
  const vv = window.visualViewport;
  if (!vv) {
    return {
      top: 0,
      left: 0,
      width: window.innerWidth,
      height: window.innerHeight,
    };
  }
  return {
    top: vv.offsetTop,
    left: vv.offsetLeft,
    width: vv.width,
    height: vv.height,
  };
}

/** Bubble toolbar that floats near a selection or user-picked element.
 *
 *  Positioning strategy:
 *  - Reads the anchor's rect each time the toolbar or window resizes
 *    (ResizeObserver + window scroll/resize listeners).
 *  - Prefers the `side` prop; flips to the opposite side if it would
 *    overflow the viewport.
 *  - Aligns on the anchor's inline axis via `align` (start/center/end).
 *  - Measures the toolbar's own rect to centre-align precisely.
 *
 *  This is a portal so the toolbar escapes any scroll/transform
 *  container, and sits above the document with z-index var(--vq-z-overlay).
 */
export function FloatingToolbar(props: FloatingToolbarProps): ReactNode {
  const {
    anchor,
    open,
    side = 'top',
    align = 'center',
    offset = 8,
    arrow = false,
    children,
    className,
  } = props;

  /* Reject negative offsets — they'd put the toolbar *inside* the
   * anchor, creating accidental overlap. */
  const safeOffset = Math.max(0, offset);

  const ref = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{
    top: number;
    left: number;
    side: 'top' | 'bottom';
    /** Distance, in px, from the toolbar's left edge to the anchor's
     *  horizontal centre. Used to position the arrow so it points at the
     *  selection rather than at the toolbar's centre — when the toolbar
     *  is clamped to the viewport edge, the centres no longer match. */
    arrowOffset: number;
  } | null>(null);
  const portalRoot = useMemo(
    () => (typeof document === 'undefined' ? null : ensurePortalRoot()),
    [],
  );

  useLayoutEffect(() => {
    if (!open || anchor === null || ref.current === null) {
      setPos(null);
      return undefined;
    }

    const compute = (): void => {
      const refEl = ref.current;
      if (refEl === null) return;
      const a = anchor.getBoundingClientRect();
      const t = refEl.getBoundingClientRect();
      const viewport = getVisibleViewport();
      const minTop = viewport.top + 4;
      const maxTop = viewport.top + viewport.height - t.height - 4;
      const minLeft = viewport.left + 4;
      const maxLeft = viewport.left + viewport.width - t.width - 4;
      let resolvedSide: 'top' | 'bottom' = side;
      let top = side === 'top' ? a.top - t.height - safeOffset : a.bottom + safeOffset;
      /* Flip if the preferred side overflows viewport. */
      if (top < minTop) {
        resolvedSide = 'bottom';
        top = a.bottom + safeOffset;
      } else if (top + t.height > viewport.top + viewport.height - 4) {
        resolvedSide = 'top';
        top = a.top - t.height - safeOffset;
      }
      /* Clamp to viewport with 4px gutter — mirrors the horizontal
       * clamp below. Both flips above can still leave the toolbar
       * overflowing when the viewport is shorter than the toolbar
       * itself, or when the anchor sits in the middle of a tight
       * viewport; clamping to [4, vh - t.height - 4] guarantees the
       * toolbar stays on screen even at the cost of slipping off the
       * anchor. */
      top = Math.max(minTop, Math.min(Math.max(minTop, maxTop), top));
      /* Alignment along horizontal axis. */
      let left: number;
      if (align === 'start') {
        left = a.left;
      } else if (align === 'end') {
        left = a.right - t.width;
      } else {
        left = a.left + (a.width - t.width) / 2;
      }
      /* Clamp to viewport with 4px gutter. */
      left = Math.max(minLeft, Math.min(Math.max(minLeft, maxLeft), left));
      /* Arrow follows the anchor's horizontal centre even when the
       * toolbar is clamped — reads as "this bubble belongs to the
       * selection", not "this bubble belongs to its own midpoint". */
      const anchorCenter = a.left + a.width / 2;
      const arrowOffset = Math.max(12, Math.min(t.width - 12, anchorCenter - left));
      setPos({ top, left, side: resolvedSide, arrowOffset });
    };

    compute();
    const onScroll = (): void => {
      compute();
    };
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onScroll);
    const vv = window.visualViewport;
    vv?.addEventListener('resize', onScroll);
    vv?.addEventListener('scroll', onScroll);
    const ro = new ResizeObserver(compute);
    ro.observe(ref.current);
    return () => {
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onScroll);
      vv?.removeEventListener('resize', onScroll);
      vv?.removeEventListener('scroll', onScroll);
      ro.disconnect();
    };
  }, [anchor, open, side, align, safeOffset]);

  useEffect(() => {
    if (!open) setPos(null);
  }, [open]);

  if (!open || portalRoot === null) return null;

  /* z-index lives in CSS (.vq-floating-toolbar) so the layer is set
   * uniformly across themes/accents and the inline style stays a clean
   * top/left/visibility triple. */
  const baseStyle: React.CSSProperties = pos
    ? { position: 'fixed', top: pos.top, left: pos.left }
    : { position: 'fixed', visibility: 'hidden', top: -9999, left: -9999 };
  const style: React.CSSProperties =
    pos && arrow
      ? { ...baseStyle, ['--vq-ft-arrow-x' as string]: `${pos.arrowOffset}px` }
      : baseStyle;

  /* Selection-preservation contract.
   *
   * The bubble toolbar is most often anchored to a contenteditable
   * selection. A `mousedown` on any toolbar surface that is NOT a
   * focusable form control would, by default, move
   * `document.activeElement` onto the toolbar — collapsing the
   * underlying selection BEFORE the click handler runs. Format
   * commands (`format.highlight`, `format.textColor`, `format.bold`,
   * …) then bail out because the selection is empty.
   *
   * Calling `preventDefault` on `mousedown` keeps focus on the editor
   * for clicks on padding, dividers, button frames, and Tooltip-anchor
   * spans — every consumer no longer has to remember the idiom. We
   * deliberately let the event through for inputs / textareas /
   * contenteditables so any future toolbar item that needs typing
   * (link editor, find-replace) can still receive caret focus. */
  const preserveSelectionOnMouseDown = (e: React.MouseEvent<HTMLDivElement>): void => {
    const target = e.target as HTMLElement | null;
    if (target?.closest('input, textarea, select, [contenteditable="true"]') !== null) {
      return;
    }
    e.preventDefault();
  };
  const preserveSelectionOnPointerDown = (e: React.PointerEvent<HTMLDivElement>): void => {
    const target = e.target as HTMLElement | null;
    if (target?.closest('input, textarea, select, [contenteditable="true"]') !== null) {
      return;
    }
    e.preventDefault();
  };

  return createPortal(
    <div
      ref={ref}
      role="toolbar"
      aria-label={props['aria-label']}
      className={['vq-floating-toolbar', className].filter(Boolean).join(' ')}
      style={style}
      data-component="FloatingToolbar"
      data-side={pos?.side ?? side}
      data-arrow={arrow ? 'true' : undefined}
      onPointerDown={preserveSelectionOnPointerDown}
      onMouseDown={preserveSelectionOnMouseDown}
    >
      <div className="vq-floating-toolbar__row">{children}</div>
      {arrow ? <span aria-hidden="true" className="vq-floating-toolbar__arrow" /> : null}
    </div>,
    portalRoot,
  );
}

/** Vertical separator between toolbar groups. Lives inside the same row
 *  as IconButtons / ColorPicker triggers and inherits the bubble's
 *  divider tone, so consumers never have to inline-style separators. */
export function FloatingToolbarDivider(): ReactNode {
  return (
    <span
      role="separator"
      aria-orientation="vertical"
      className="vq-floating-toolbar__divider"
    />
  );
}
